import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State, Tenants } from '../../../store';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import { EnquiriesService } from '@careapp-group/ng-sdk';
import { TenantSelectors } from '../../../store/tenants/tenants.selectors';
import { EnquiriesTenantCategories } from './enquiries-tenant-categories.actions';
import { EnquiriesCategoryWrapper } from './enquiries-tenant-categories.reducer';
import { Injectable } from '@angular/core';
import { EnquiriesTenantCategorySelectors } from './enquiries-tenant-categories.selectors';

@Injectable()
export class EnquiriesTenantCategoriesEffects {
  getTenantCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiriesTenantCategories.RequestAllForSegmentAction),
      switchMap((action) => {
        return this.enquiriesService
          .listEnquiriesTenantCategories({ tenantId: action.segment })
          .pipe(
            map((result) =>
              EnquiriesTenantCategories.ResponseSuccessAllForSegmentAction({
                segment: action.segment,
                filter: action.filter,
                items: result.map((r) => new EnquiriesCategoryWrapper(r)),
              }),
            ),
            catchError((error) =>
              of(
                EnquiriesTenantCategories.ResponseFailAllForSegmentAction({
                  error: error,
                  segment: action.segment,
                }),
              ),
            ),
          );
      }),
    ),
  );

  addCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiriesTenantCategories.RequestCreateAction),
      withLatestFrom(this.store.select(TenantSelectors.getSelectedId)),
      switchMap(([action, tenantId]) =>
        this.enquiriesService
          .createEnquiriesCategory({
            createEnquiriesCategoryDto: action.payload,
            tenantId,
          })
          .pipe(
            map((result) =>
              EnquiriesTenantCategories.ResponseSuccessCreateAction({
                payload: new EnquiriesCategoryWrapper(result),
              }),
            ),
            catchError((error) =>
              of(
                EnquiriesTenantCategories.ResponseFailCreateAction({
                  error: error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnquiriesTenantCategories.RequestDeleteByIdAction),
      withLatestFrom(this.store.select(EnquiriesTenantCategorySelectors.getEntities)),
      switchMap(([action, categories]) =>
        this.enquiriesService
          .deleteEnquiriesCategory({
            tenantId: categories[action.id].tenantId,
            createEnquiriesCategoryDto: {
              label: categories[action.id].label,
            },
          })
          .pipe(
            map((result) =>
              EnquiriesTenantCategories.ResponseSuccessDeleteByIdAction({
                id: action.id,
              }),
            ),
            catchError((error) =>
              of(
                EnquiriesTenantCategories.ResponseFailDeleteByIdAction({
                  error: error,
                  id: action.id,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  getTenantCategoriesOnTenantSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Tenants.SelectByIdAction),
      withLatestFrom(this.store.select(TenantSelectors.getSelected)),
      filter(([action, tenant]) => tenant?.plan?.hasEnquiries),
      map(([action, selected]) =>
        EnquiriesTenantCategories.RequestAllForSegmentAction({
          segment: action.id,
          clear: true,
          filter: {},
        }),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private enquiriesService: EnquiriesService,
    private store: Store<State>,
  ) {}
}
